import { useEffect, useMemo } from "react";
import { useShallow } from "zustand/react/shallow";
import type { Space } from "@/lib/api/organizations";
import { useMainStateStore } from "@/components/main/stores/mainStateStore";
import {
  spaceTrail,
  useOrgContextStore,
  visibleSpaces,
} from "@/components/main/stores/orgContextStore";

/// The model's space, root-first; empty for a catalog model or a space outside
/// the active org's tree.
export function useModelSpaceTrail(modelId: string | undefined): Space[] {
  const { spaces, activeOrgId, orgsLoaded } = useOrgContextStore(
    useShallow((s) => ({
      spaces: s.spaces,
      activeOrgId: s.activeOrgId,
      orgsLoaded: s.orgsLoaded,
    })),
  );
  const { spaceId, listOrgId, fetchModelsList } = useMainStateStore(
    useShallow((s) => ({
      spaceId: s.availableModels.find((m) => m.model_id === modelId)?.space_id,
      listOrgId: s.availableModelsOrgId,
      fetchModelsList: s.fetchModelsList,
    })),
  );

  // Only the app shell loads the models list, so a canvas opened from a link
  // has none; asked before the org is restored it would list the personal one.
  useEffect(() => {
    if (orgsLoaded && activeOrgId && listOrgId !== activeOrgId) {
      void fetchModelsList({ refresh: true });
    }
  }, [orgsLoaded, activeOrgId, listOrgId, fetchModelsList]);

  return useMemo(
    () => (spaceId ? spaceTrail(visibleSpaces(spaces), spaceId) : []),
    [spaces, spaceId],
  );
}
